import { create } from 'zustand';
import type {
  EggTierView,
  EggView,
  CollectionSummaryView,
  RewardsSummaryView,
  HatchResultView,
} from '@/types/rewards';

interface RewardsState {
  // Reference data (fetched once per session)
  eggTiers: EggTierView[];

  // Reader-specific state
  activeEggs: EggView[];
  collection: CollectionSummaryView | null;
  summary: RewardsSummaryView | null;
  dailyClaimed: boolean;

  // UI state
  hatchingEggId: string | null;
  revealResult: HatchResultView | null;
  detailCreatureId: string | null;

  // Actions
  setEggTiers: (tiers: EggTierView[]) => void;
  setActiveEggs: (eggs: EggView[]) => void;
  addEgg: (egg: EggView) => void;
  removeEgg: (eggId: string) => void;
  setCollection: (c: CollectionSummaryView) => void;
  setSummary: (s: RewardsSummaryView) => void;
  setDailyClaimed: (v: boolean) => void;
  setHatchingEggId: (id: string | null) => void;
  setRevealResult: (r: HatchResultView | null) => void;
  setDetailCreatureId: (id: string | null) => void;
  reset: () => void;
}

const initialState = {
  eggTiers: [] as EggTierView[],
  activeEggs: [] as EggView[],
  collection: null as CollectionSummaryView | null,
  summary: null as RewardsSummaryView | null,
  dailyClaimed: false,
  hatchingEggId: null as string | null,
  revealResult: null as HatchResultView | null,
  detailCreatureId: null as string | null,
};

export const useRewardsStore = create<RewardsState>()((set) => ({
  ...initialState,

  setEggTiers: (eggTiers) => set({ eggTiers }),
  setActiveEggs: (activeEggs) => set({ activeEggs }),
  addEgg: (egg) => set((s) => ({ activeEggs: [...s.activeEggs, egg] })),
  removeEgg: (eggId) =>
    set((s) => ({
      activeEggs: s.activeEggs.filter((e) => e.id !== eggId),
    })),
  setCollection: (collection) => set({ collection }),
  setSummary: (summary) => set({ summary }),
  setDailyClaimed: (dailyClaimed) => set({ dailyClaimed }),
  setHatchingEggId: (hatchingEggId) => set({ hatchingEggId }),
  setRevealResult: (revealResult) => set({ revealResult }),
  setDetailCreatureId: (detailCreatureId) => set({ detailCreatureId }),
  reset: () => set(initialState),
}));
